import Select from 'react-select'
import { ReactComponent as ClockIcon } from '../../assets/arv/time-within.svg'
import { ReactComponent as TargetIcon } from '../../assets/arv/radius.svg'
import { ReactComponent as SearchIcon } from '../../assets/arv/search.svg'

const PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  // { value: 'multi_family',  label: 'Multi Family' },
  // { value: 'condo',         label: 'Condo' },
  // { value: 'townhouse',     label: 'Townhouse' },
]

const selectStyles = {
  control: (base) => ({
    ...base,
    border: '1px solid #DAE4D5',
    borderRadius: 8,
    boxShadow: 'none',
    minHeight: 40,
    fontSize: 13,
    cursor: 'pointer',
    backgroundColor: 'white',
    '&:hover': { borderColor: '#4CBC18' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#E7F0E2' : state.isFocused ? '#f8fef4' : '#fff',
    color: '#17270D',
    fontSize: 13,
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({ ...base, caretColor: 'transparent' }),
}

const labelCls = 'block text-[12px] text-brand-secondary mb-[4px]'

const inputBoxCls = `
  h-[40px] rounded-md border border-brand-muted bg-white
  text-[13px] text-brand-dark placeholder:text-brand-unselectedGray
  focus:outline-none focus:border-brand-green px-[8px]
  hover:border-brand-green
`.trim()

export default function SearchCriteria({ criteria, onCriteriaChange, onCriteriaBlur, onSearch, searching, lastSearchedAt }) {
  const set = (field) => (e) => onCriteriaChange({ ...criteria, [field]: e.target.value })

  return (
    <div>
      <p className="text-[14px] font-semibold text-brand-dark mb-[12px]">Search Criteria</p>

      {/* Row 1 — property specs */}
      <div className="flex items-end gap-x-[16px]">

        <div className="flex flex-col" style={{ width: 168 }}>
          <span className={labelCls}>Property Type</span>
          <Select
            value={PROPERTY_TYPES.find(o => o.value === criteria.propertyType) ?? null}
            options={PROPERTY_TYPES}
            onChange={(opt) => {
              const next = { ...criteria, propertyType: opt?.value ?? '' }
              onCriteriaChange(next)
              onCriteriaBlur?.(next)
            }}
            styles={selectStyles}
            menuPortalTarget={document.body}
            isSearchable={false}
            isClearable={false}
          />
        </div>

        <div className="flex flex-col">
          <span className={labelCls}>Beds</span>
          <input
            type="number" min="0"
            value={criteria.beds ?? ''}
            onChange={set('beds')}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Any"
            style={{ width: 48 }}
            className={inputBoxCls + ' text-center'}
          />
        </div>

        <div className="flex flex-col">
          <span className={labelCls}>Baths</span>
          <input
            type="number" min="0"
            value={criteria.baths ?? ''}
            onChange={set('baths')}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Any"
            style={{ width: 48 }}
            className={inputBoxCls + ' text-center'}
          />
        </div>

        <div className="flex flex-col">
          <span className={labelCls}>Sqft</span>
          <input
            type="number" min="0"
            value={criteria.sqftMin ?? ''}
            onChange={set('sqftMin')}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Min"
            style={{ width: 72 }}
            className={inputBoxCls  + ' text-center'}
          />
        </div>

        <div className="flex flex-col">
          <span className={labelCls}>Year Built</span>
          <input
            type="number" min="1800" max="2099"
            value={criteria.yearBuiltMin ?? ''}
            onChange={set('yearBuiltMin')}
            onBlur={() => onCriteriaBlur?.(criteria)}
            placeholder="Min"
            style={{ width: 72 }}
            className={inputBoxCls +' text-center' }
          />
        </div>

      </div>

      {/* Row 2 — comps window + radius + search */}
      <div className="flex items-end gap-x-[16px] mt-[15px]">

        {/* Radius */}
        <div className="flex flex-col" style={{ width: 150 }}>
          <span className={labelCls}>Radius</span>
          <div className={inputBoxCls + ' flex items-center justify-between px-[8px]'} style={{ width: 150 }}>
            <div className="flex items-center gap-x-[6px]">
              <TargetIcon />
              <input
                type="number" min="0.4" max="1" step="0.1"
                value={criteria.radiusMiles ?? '1'}
                onChange={set('radiusMiles')}
                onBlur={() => onCriteriaBlur?.(criteria)}
                className="w-[36px] text-[13px] text-brand-dark focus:outline-none bg-transparent text-center"
              />
            </div>
            <span className="text-[13px] text-brand-disabled">mi</span>
          </div>
        </div>
        
        {/* Find Comps Within */}
        <div className="flex flex-col" style={{ width: 150 }}>
          <span className={labelCls}>Find Comps Within</span>
          <div className={inputBoxCls + ' flex items-center justify-between px-[8px]'} style={{ width: 150 }}>
            <div className="flex items-center gap-x-[6px]">
              <ClockIcon />
              <input
                type="number" min="1" max="12"
                value={criteria.monthsWithin ?? '12'}
                onChange={set('monthsWithin')}
                onBlur={() => onCriteriaBlur?.(criteria)}
                className="w-[36px] text-[13px] text-brand-dark focus:outline-none bg-transparent text-center"
              />
            </div>
            <span className="text-[13px] text-brand-disabled">months</span>
          </div>
        </div>

        {/* Search button — aligned with input boxes, no label above */}
        <button
          onClick={onSearch}
          disabled={searching}
          style={{ width: 141, height: 40 }}
          className="rounded-full bg-brand-green text-white text-[13px] font-semibold flex items-center justify-center gap-x-[6px] hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <SearchIcon />
          {searching ? 'Analyzing…' : 'Search'}
        </button>

      </div>
    </div>
  )
}
